import { Button, Switch } from '@features/ui';
import {
  getAskAliseVisibilityConfig,
  updateAskAliseVisibilityConfig,
} from '@src/services/systemSettings';
import { useAuthStore } from '@src/stores/authStore';
import { UserRolePermission } from '@src/types/settings';
import { canAccess } from '@src/utils/auth';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

export const MintSettingsPage = () => {
  const { auth } = useAuthStore();
  const [allowAskAlise, setAllowAskAlise] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const canManageSystemSettings = useMemo(
    () =>
      canAccess(
        UserRolePermission.MANAGE_SYSTEM_SETTINGS,
        auth?.user?.permissions
      ),
    [auth?.user?.permissions]
  );

  useEffect(() => {
    let mounted = true;

    const loadConfig = async () => {
      try {
        const { data } = await getAskAliseVisibilityConfig();

        if (mounted) {
          setAllowAskAlise(data.allow_ask_alise);
        }
      } catch {
        toast.error('Failed to load Ask Alise setting.');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadConfig().then();

    return () => {
      mounted = false;
    };
  }, []);

  const handleSave = async () => {
    if (!canManageSystemSettings) {
      return;
    }

    try {
      setSaving(true);
      await updateAskAliseVisibilityConfig({
        allow_ask_alise: allowAskAlise,
      });
      toast.success('System setting updated successfully.');
    } catch {
      toast.error('Failed to update Ask Alise setting.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={'flex flex-col gap-y-8'}>
      <div className={'flex items-center justify-between gap-x-2'}>
        <h1 className={'text-[23px]'}>Mint Settings</h1>
      </div>

      <div className={'rounded-lg border border-border p-5 flex flex-col gap-y-4 max-w-[520px]'}>
        <div className={'flex items-center justify-between gap-x-4'}>
          <div>
            <h2 className={'text-base font-medium'}>Ask Alise Menu</h2>
            <p className={'text-sm text-muted-foreground'}>
              Allow showing Ask Alise menu for users in sidebar.
            </p>
          </div>
          <Switch
            checked={allowAskAlise}
            disabled={loading || saving || !canManageSystemSettings}
            onCheckedChange={(checked) => setAllowAskAlise(Boolean(checked))}
          />
        </div>

        <div className={'flex justify-end'}>
          <Button
            disabled={loading || saving || !canManageSystemSettings}
            onClick={handleSave}
            size={'sm'}
            variant={'lime'}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
};
